import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

const getAuthImageKey = (logoUrl, backgroundUrl) => JSON.stringify([
  logoUrl || null,
  backgroundUrl || null,
]);

const preloadImage = (url) => new Promise((resolve) => {
  const image = new Image();
  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    resolve();
  };

  image.onload = settle;
  image.onerror = settle;
  image.src = url;

  if (image.complete) {
    const decoded = image.decode?.();
    if (decoded?.then) {
      decoded.then(settle, settle);
    } else {
      settle();
    }
  }
});

export const useStoreAuthBrandingReady = ({ logoUrl, backgroundUrl } = {}) => {
  const imageKey = getAuthImageKey(logoUrl, backgroundUrl);
  const hasImages = Boolean(logoUrl || backgroundUrl);
  const [loadedKey, setLoadedKey] = useState(null);

  useEffect(() => {
    const imageUrls = [logoUrl, backgroundUrl].filter(Boolean);
    if (imageUrls.length === 0) {
      setLoadedKey(imageKey);
      return undefined;
    }

    let cancelled = false;
    Promise.all(imageUrls.map(preloadImage)).then(() => {
      if (!cancelled) {
        setLoadedKey(imageKey);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [backgroundUrl, imageKey, logoUrl]);

  return !hasImages || loadedKey === imageKey;
};

export const getStoreAuthBackgroundSx = (backgroundUrl) => (
  backgroundUrl
    ? {
        backgroundImage: `url("${backgroundUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        '&::before': { display: 'none' },
        '&::after': { display: 'none' },
      }
    : {}
);

const StoreAuthLogo = ({ imageUrl, storeName, sx }) => {
  if (!imageUrl) return null;

  return (
    <Box
      component="img"
      src={imageUrl}
      alt={storeName || undefined}
      sx={{
        display: 'block',
        width: '100%',
        maxWidth: { xs: 250, sm: 300 },
        height: { xs: 72, sm: 88 },
        objectFit: 'contain',
        objectPosition: 'center',
        mb: '1.5rem',
        ...sx,
      }}
    />
  );
};

export default StoreAuthLogo;
